/**
 * CmsPageView — public renderer for /p/:slug (and deep-slugs like
 * /p/help/getting-started via a catch-all).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { cmsApi } from "@/lib/cmsApi";
import { resolveSiteSlug } from "@/config/siteConfig";
import StoryShareCard from "@/components/newspulse/StoryShareCard";

export default function CmsPageView() {
  const params = useParams();
  const slug = params["*"] || params.slug || "";
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setPage(null); setErr(null);
    const portal = resolveSiteSlug() || "socialpulse";
    cmsApi.publicPage(portal, slug).then(setPage).catch(async (e) => {
      // Fallback: try socialpulse portal when the per-portal lookup misses.
      if (portal !== "socialpulse") {
        try { const alt = await cmsApi.publicPage("socialpulse", slug); setPage(alt); return; } catch {}
      }
      setErr(e.message || "Page not found");
    });
  }, [slug]);

  useEffect(() => {
    if (page?.title) document.title = page.title;
    if (page?.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = page.meta_description;
    }
  }, [page]);

  if (err) return (
    <div className="max-w-[720px] mx-auto px-4 py-24 text-center" data-testid="cms-view-error">
      <AlertCircle className="w-8 h-8 text-rose-600 mx-auto mb-3" />
      <p className="text-sm text-slate-700">{err}</p>
    </div>
  );
  if (!page) return (
    <div className="max-w-[720px] mx-auto px-4 py-24 flex items-center justify-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );

  return (
    <article className="max-w-3xl mx-auto px-4 lg:px-6 py-12" data-testid="cms-view-page">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6 leading-tight"
          style={{ fontFamily: "'Lora', 'Georgia', serif" }}
          data-testid="cms-view-title">
        {page.title}
      </h1>
      <div className="cms-rendered prose prose-slate max-w-none"
           data-testid="cms-view-body"
           dangerouslySetInnerHTML={{ __html: page.body_html }} />
      <div className="mt-10">
        <StoryShareCard
          url={typeof window !== "undefined" ? window.location.href : `/p/${page.slug}`}
          title={page.title}
          summary={page.meta_description || ""}
          hashtag="#SocialPulse"
          shortUrlPortal={page.portal_slug || "socialpulse"}
        />
      </div>
      <style>{`
        .cms-rendered img.rte-image { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.5rem 0; }
        .cms-rendered img.rte-image-left  { float: left;  margin: 0.25rem 1.25rem 0.5rem 0; max-width: 50%; }
        .cms-rendered img.rte-image-right { float: right; margin: 0.25rem 0 0.5rem 1.25rem; max-width: 50%; }
        .cms-rendered img.rte-image-center { display: block; margin: 0.75rem auto; max-width: 90%; float: none; }
        .cms-rendered p::after { content: ""; display: table; clear: both; }
        .cms-rendered iframe.rte-youtube { max-width: 100%; aspect-ratio: 16/9; margin: 1rem 0; border-radius: 0.5rem; }
      `}</style>
    </article>
  );
}
